import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface AuthState {
  user_id: string;
  group_id: string;
  isLogin: boolean;
  lang: string;
  rememberMe: boolean;
  // Authorization scope of the account, from /authentication/login and
  // refreshed by /authentication/check-status. 'healthcare' = a hospital web
  // account (health_care_facility.user_id_website), which may only see its own
  // facility; 'internal' = agency / PSC operator / root, no restriction beyond
  // the organization. Screens use it to hide what the caller cannot use - the
  // backend is the one that enforces it (services/common/user_scope.py).
  account_type: 'internal' | 'healthcare';
  health_care_facility_id: number | null;
  health_care_facility_name: string;
  health_care_facility_ids: number[];
}

interface AccountScopeInput {
  account_type?: 'internal' | 'healthcare';
  health_care_facility_id?: number | null;
  health_care_facility_name?: string | null;
  health_care_facility_ids?: number[];
}

interface AuthActions {
  setAuth: (data: { user_id: string; group_id: number; lang: string; rememberMe: boolean }) => void;
  setAccountScope: (data: AccountScopeInput) => void;
  clearAuth: () => void;
  setLang: (lang: string) => void;
  sendLogoutSignal: () => void;
}

const initialState: AuthState = {
  user_id: '',
  group_id: '',
  isLogin: false,
  lang: 'eng',
  rememberMe: false,
  account_type: 'internal',
  health_care_facility_id: null,
  health_care_facility_name: '',
  health_care_facility_ids: [],
};

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      ...initialState,

      setAuth: ({ user_id, group_id, lang, rememberMe }) =>
        set({
          user_id,
          group_id: String(group_id),
          isLogin: true,
          lang,
          rememberMe,
        }),

      // Missing keys mean "not a hospital account": an older backend that does
      // not send the scope yet must not leave a stale facility behind.
      setAccountScope: ({
        account_type,
        health_care_facility_id,
        health_care_facility_name,
        health_care_facility_ids,
      }) =>
        set({
          account_type: account_type === 'healthcare' ? 'healthcare' : 'internal',
          health_care_facility_id: health_care_facility_id ?? null,
          health_care_facility_name: health_care_facility_name ?? '',
          health_care_facility_ids: health_care_facility_ids ?? [],
        }),

      clearAuth: () => {
        // Clean up legacy keys from before zustand migration
        localStorage.removeItem('user_id');
        localStorage.removeItem('group_id');
        localStorage.removeItem('isLogin');
        localStorage.removeItem('lang');
        sessionStorage.removeItem('user_id');
        sessionStorage.removeItem('group_id');
        sessionStorage.removeItem('isLogin');

        set(initialState);
      },

      setLang: (lang: string) => set({ lang }),

      sendLogoutSignal: () => {
        // Write directly to localStorage for cross-tab StorageEvent detection
        const signal = JSON.stringify({ value: false, time: Date.now() });
        localStorage.setItem('logout_signal', signal);
      },
    }),
    {
      name: 'auth',
      storage: createJSONStorage(() => {
        // Use localStorage for persistence (remember_me is handled by the store itself)
        // When rememberMe=false, clearAuth is called on window close via beforeunload
        return localStorage;
      }),
      partialize: (state) => ({
        user_id: state.user_id,
        group_id: state.group_id,
        isLogin: state.isLogin,
        lang: state.lang,
        rememberMe: state.rememberMe,
        account_type: state.account_type,
        health_care_facility_id: state.health_care_facility_id,
        health_care_facility_name: state.health_care_facility_name,
        health_care_facility_ids: state.health_care_facility_ids,
      }),
    }
  )
);

// Clear non-remembered sessions on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    const state = useAuthStore.getState();
    if (state.isLogin && !state.rememberMe) {
      localStorage.removeItem('auth');
    }
  });
}
